import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type { TransactionCellProps, TransactionCellStatus } from "./types";

const renderIcon = (icon: React.ReactNode, size: number, color: string) => {
  if (!React.isValidElement(icon)) return icon;

  return React.cloneElement(
    icon as React.ReactElement<{ size?: number | string; color?: string }>,
    {
      size,
      color,
    }
  );
};

export const TransactionCell: React.FC<TransactionCellProps> = ({
  title,
  amount,
  description,
  secondaryAmount,
  icon,
  status = "completed",
  currency = "fiat",
  state = "default",
  onPress,
  disabled,
  "aria-label": ariaLabel,
  testID,
  themeMode,
  themeProductContext = "b2c",
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const isInteractive = Boolean(onPress) && !disabled;

  const stateBackground =
    state === "press"
      ? theme.colors.overlay.brand || theme.colors.overlay.mono
      : theme.colors.overlay.mono;
  const hoverBackground = theme.colors.background.secondary;

  const getAmountColor = (nextStatus: TransactionCellStatus) => {
    if (nextStatus === "pending") return theme.colors.content.secondary;
    if (nextStatus === "failed") return theme.colors.content.alert.primary;
    if (
      typeof amount === "string" &&
      (amount.trim().startsWith("+") || currency === "xsollaPoints")
    ) {
      return theme.colors.content.success.primary;
    }
    return theme.colors.content.primary;
  };

  return (
    <Box
      width="100%"
      flexDirection="row"
      alignItems="center"
      gap={8}
      padding={12}
      borderRadius={8}
      borderWidth={1}
      borderStyle="solid"
      borderColor={theme.colors.border.secondary}
      backgroundColor={stateBackground}
      cursor={isInteractive ? "pointer" : undefined}
      opacity={disabled ? 0.5 : 1}
      role={isInteractive ? "button" : "group"}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onPress={isInteractive ? onPress : undefined}
      hoverStyle={
        isInteractive && state === "default"
          ? { backgroundColor: hoverBackground }
          : undefined
      }
      pressStyle={{ backgroundColor: theme.colors.overlay.brand }}
      testID={testID || "transaction-cell"}
    >
      <Box
        width={48}
        height={48}
        flexShrink={0}
        alignItems="center"
        justifyContent="center"
        borderRadius={4}
        backgroundColor={theme.colors.overlay.mono}
      >
        {icon && renderIcon(icon, 24, theme.colors.content.primary)}
      </Box>
      <Box flex={1} minWidth={0} flexDirection="column" justifyContent="center">
        <Text
          color={theme.colors.content.primary}
          fontFamily={theme.fonts.body}
          fontSize={14}
          lineHeight={18}
          fontWeight="500"
          numberOfLines={1}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Text>
        {description && (
          <Text
            color={theme.colors.content.secondary}
            fontFamily={theme.fonts.body}
            fontSize={12}
            lineHeight={16}
            fontWeight="400"
            numberOfLines={1}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description}
          </Text>
        )}
      </Box>
      <Box
        flexShrink={0}
        flexDirection="column"
        alignItems="flex-end"
        justifyContent="center"
      >
        <Text
          color={getAmountColor(status)}
          fontFamily={theme.fonts.body}
          fontSize={16}
          lineHeight={20}
          fontWeight="500"
          textAlign="right"
          numberOfLines={1}
        >
          {amount}
        </Text>
        {secondaryAmount && (
          <Text
            color={theme.colors.content.secondary}
            fontFamily={theme.fonts.body}
            fontSize={12}
            lineHeight={16}
            fontWeight="400"
            textAlign="right"
            numberOfLines={1}
          >
            {secondaryAmount}
          </Text>
        )}
      </Box>
    </Box>
  );
};
